import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../../store/store";
import { STATUS } from "../../../../store/slices/OrganizationSlice";
import { fetchSupportDebugCashflowAsync } from "../../../../store/thunks/SupportDebugThunk";
import { CashflowShortfallResponse } from "../../../../services/SupportDebugService";
import { Badge } from "../../../Catalyst/badge";
import { Button } from "../../../Catalyst/button";
import { Input } from "../../../Catalyst/input";
import { Field, Label } from "../../../Catalyst/fieldset";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../Catalyst/table";
import BillsTable from "../BillsTable";
import { PanelErrorCard, PanelLoadingCard } from "../PanelStates";
import {
  categorySkipNote,
  eligibilitySkipReasonLabel,
  formatCurrency,
  formatDate,
  spendingHealthStateDisplay,
} from "../utils";

interface CashflowShortfallPanelProps {
  userId: string;
}

// Everything below renders verbatim from the API response — the widget block is
// what the app derives, the algorithm block is the raw engine output.
const CashflowResult = ({ data }: { data: CashflowShortfallResponse }) => {
  const { widget, algorithm } = data;

  if (!widget.eligible) {
    return (
      <div className="mt-6 w-full rounded-lg border-2 border-amber-200 bg-amber-50 pt-5">
        <div className="flex items-start gap-3 px-6 pb-5">
          <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-amber-500" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              This user doesn&rsquo;t qualify for the Spending Health widget — the app shows Safe
              to Spend instead
            </p>
            <p className="mt-1 text-sm text-amber-800">
              Reason: {eligibilitySkipReasonLabel(widget.skipReason)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const stateDisplay = spendingHealthStateDisplay(widget.state);
  // Threshold percent comes from the response (contract guarantees it when
  // eligible=true) — never hardcode it in copy.
  const thresholdPctLabel = `${((widget.thresholdPct ?? 0) * 100).toFixed(0)}%`;

  return (
    <>
      {/* (a) What the user sees */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="px-6 pb-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="desktext11 text-texthead">What the user sees</div>
            <Badge color={stateDisplay.color}>{stateDisplay.label}</Badge>
          </div>
          {/* heroNumber contains a true minus sign (U+2212) — render verbatim */}
          <div className="mt-4 text-4xl font-semibold tabular-nums text-texthead">
            {widget.heroNumber}
          </div>
          <div className="mt-2 text-base font-medium text-texthead">{widget.header}</div>
          <p className="mt-1 text-sm text-textColor">{widget.subtext}</p>
          {widget.goalName != null && (
            <p className="mt-3 text-sm text-textColor3">
              Goal: <span className="font-medium text-texthead">{widget.goalName}</span>
              {widget.goalTargetThisPeriod != null && (
                <> — target this period {formatCurrency(widget.goalTargetThisPeriod)}</>
              )}
            </p>
          )}
          <dl className="mt-4 space-y-3 border-t border-shadowborder pt-4 text-sm">
            <div>
              <dt className="font-medium text-textColor3">AI chat prompt</dt>
              <dd className="mt-0.5 text-textColor">{widget.aiChatPrompt}</dd>
            </div>
            <div>
              <dt className="font-medium text-textColor3">Info tooltip</dt>
              <dd className="mt-0.5 text-textColor">{widget.infoTooltip}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* (b) Why this state */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="px-6 pb-5">
          <div className="desktext11 text-texthead">Why this state</div>
          <p className="mt-1 text-sm text-textColor3">
            {thresholdPctLabel} band = &plusmn;{formatCurrency(widget.threshold)} (
            {thresholdPctLabel} of projected income)
          </p>
          <ul className="mt-4 space-y-2">
            {(widget.stateLadder ?? []).map((row) => {
              const rowDisplay = spendingHealthStateDisplay(row.state);
              return (
                <li
                  key={row.state}
                  className={`flex items-start justify-between gap-4 rounded-lg border px-4 py-3 ${
                    row.matched
                      ? "border-green-500 bg-green-50 ring-1 ring-green-500"
                      : "border-shadowborder"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {row.matched && <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-600" />}
                      <span className="text-sm font-medium text-texthead">{rowDisplay.label}</span>
                    </div>
                    <p className="mt-0.5 text-sm text-textColor">{row.rule}</p>
                  </div>
                  <Badge color={rowDisplay.color}>{row.state}</Badge>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* (c) The math */}
      {data.formula && (
        <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
          <div className="px-6">
            <div className="desktext11 text-texthead">The math</div>
            <p className="mt-1 font-mono text-xs text-textColor3">{data.formula.expression}</p>
            {widget.payPeriod && (
              // daysElapsed is a 0-based completed-days counter (0 on payday),
              // so phrase it as elapsed/remaining rather than "day N of M".
              <p className="mt-2 text-sm text-textColor3">
                Pay period: {formatDate(widget.payPeriod.start)} &rarr;{" "}
                {formatDate(widget.payPeriod.end)} — {widget.payPeriod.daysElapsed} day(s) elapsed
                &middot; {widget.payPeriod.daysRemaining} remaining
              </p>
            )}
          </div>
          <div className="mt-4 divide-y divide-shadowborder border-t border-shadowborder">
            {data.formula.components.map((component) => (
              <div key={component.key} className="flex items-center justify-between gap-6 px-6 py-3">
                <span className="text-sm text-texthead">{component.label}</span>
                <span className="shrink-0 text-right text-sm tabular-nums text-texthead">
                  {formatCurrency(component.value)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between gap-6 px-6 py-3">
              <span className="text-sm font-semibold text-texthead">Shortfall value</span>
              <span className="shrink-0 text-right text-base font-bold tabular-nums text-texthead">
                {formatCurrency(data.formula.result.value)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* (d) Per-category projection */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="desktext11 px-6 pb-4 text-texthead">Per-category projection</div>
        {algorithm.category_detail.length === 0 ? (
          <div className="flex items-center justify-center px-6 py-10">
            <p className="text-sm font-medium text-texthead opacity-70">No category projections</p>
          </div>
        ) : (
          <div className="px-6 pb-4">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Category</TableHeader>
                  <TableHeader>Kind</TableHeader>
                  <TableHeader className="text-right">Spent</TableHeader>
                  <TableHeader className="text-right">Burn rate</TableHeader>
                  <TableHeader className="text-right">Monthly limit</TableHeader>
                  <TableHeader className="text-right">Monthly bills</TableHeader>
                  <TableHeader className="text-right">Discretionary</TableHeader>
                  <TableHeader className="text-right">Period cap</TableHeader>
                  <TableHeader className="text-right">Projected</TableHeader>
                  <TableHeader>Note</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {algorithm.category_detail.map((row) => (
                  <TableRow key={row.catg_id}>
                    <TableCell className="font-medium text-texthead">
                      {row.category_name || `Category ${row.catg_id}`}
                    </TableCell>
                    <TableCell>
                      <Badge color={row.kind === "elastic" ? "blue" : "zinc"}>{row.kind}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.spent)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.burn_rate)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.monthly_limit)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.monthly_recurring)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.discretionary_limit)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.pay_period_cap)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={`Source: ${row.source}`}>
                      {formatCurrency(row.projected)}
                    </TableCell>
                    <TableCell className="text-textColor">{categorySkipNote(row.skip_reason)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* (e) Bills paid / bills remaining */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="desktext11 px-6 pb-4 text-texthead">Bills already paid this period</div>
        <BillsTable
          bills={data.billsPaidList}
          emptyMessage="No bills have landed yet this pay period"
        />
      </div>
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="desktext11 px-6 pb-4 text-texthead">Bills still due this period</div>
        <BillsTable
          bills={widget.billsRemainingList ?? []}
          // A positive remaining total with no itemized rows means the two
          // reads disagreed — say so instead of claiming there are no bills.
          emptyMessage={
            (widget.billsRemaining ?? 0) > 0
              ? `Total shows ${formatCurrency(widget.billsRemaining)} but no itemized bills were returned — data may have changed between reads; refresh to reconcile.`
              : "No bills remaining this pay period"
          }
        />
      </div>

      {/* (f) Daily spend */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="desktext11 px-6 pb-4 text-texthead">Daily spend (last 7 days)</div>
        {(widget.dailySpendLast7Days ?? []).length === 0 ? (
          <div className="flex items-center justify-center px-6 py-10">
            <p className="text-sm font-medium text-texthead opacity-70">No daily spend data</p>
          </div>
        ) : (
          <div className="px-6">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Date</TableHeader>
                  <TableHeader className="text-right">Spent</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {(widget.dailySpendLast7Days ?? []).map((day) => (
                  <TableRow key={day.date}>
                    <TableCell>{formatDate(day.date)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(day.spent)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <p className="px-6 pb-4 pt-2 text-xs text-textColor3">
          Rolling window ending today — not affected by the as-of date.
        </p>
      </div>
    </>
  );
};

const CashflowShortfallPanel = ({ userId }: CashflowShortfallPanelProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, status, error, requestedAsOf } = useSelector(
    (state: RootState) => state.SupportDebugStore.cashflow
  );
  // Empty means "today" (the endpoint's default when asOfDate is omitted).
  // Initialize from the asOfDate the current data was requested with so
  // re-entering the tab shows the input matching what's on screen.
  const [asOfDate, setAsOfDate] = useState(requestedAsOf ?? "");

  useEffect(() => {
    if (userId && status === STATUS.IDLE) {
      dispatch(fetchSupportDebugCashflowAsync({ userId }));
    }
  }, [userId, status, dispatch]);

  const handleAsOfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setAsOfDate(value);
    dispatch(fetchSupportDebugCashflowAsync({ userId, asOfDate: value || undefined }));
  };

  const handleResetAsOf = () => {
    setAsOfDate("");
    dispatch(fetchSupportDebugCashflowAsync({ userId }));
  };

  return (
    <>
      <div className="mt-6 flex flex-wrap items-end gap-3">
        <Field className="w-56">
          <Label>As of date</Label>
          <Input type="date" value={asOfDate} onChange={handleAsOfChange} aria-label="As of date" />
        </Field>
        {asOfDate && (
          <Button outline onClick={handleResetAsOf}>
            Reset to today
          </Button>
        )}
        {data && status === STATUS.SUCCESS && (
          <p className="pb-2 text-sm text-textColor3">Computed as of {formatDate(data.asOf)}</p>
        )}
      </div>

      {(status === STATUS.LOADING || status === STATUS.IDLE) && <PanelLoadingCard />}
      {status === STATUS.FAILED && <PanelErrorCard error={error} />}
      {status === STATUS.SUCCESS && data && <CashflowResult data={data} />}
    </>
  );
};

export default CashflowShortfallPanel;
